import { TEAM_ICON_NAMES, isTeamIconName, type TeamIconName } from "@yagni/shared";

/**
 * The Team's effective line icon: a user-chosen `icon` (stored on the desk,
 * picked in Team settings) wins; otherwise a stable default derived from the
 * team's name (keyword match first, then a deterministic rotation) — honest
 * about being a default, consistent across renders.
 */
export function defaultTeamIcon(desk: {
  id: string;
  name: string;
  icon?: string | null;
}): TeamIconName {
  if (isTeamIconName(desk.icon)) return desk.icon;
  const name = desk.name.toLowerCase();
  if (/(reliab|infra|secur|platform|ops|sre|eng)/.test(name)) return "shield";
  if (/(checkout|commerce|billing|sales|revenue|shop)/.test(name)) return "cart";
  if (/(growth|market|analytic|metric|funnel|seo)/.test(name)) return "trend";
  if (/(support|success|comms|communit|help)/.test(name)) return "chat";
  const icons: TeamIconName[] = ["shield", "cart", "trend", "chat"];
  let hash = 0;
  for (const ch of desk.id) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return icons[Math.abs(hash) % icons.length]!;
}

const PATHS: Record<TeamIconName, React.ReactNode> = {
  shield: <path d="M12 3l7 3v5c0 4.6-3 7.6-7 9-4-1.4-7-4.4-7-9V6l7-3z" />,
  cart: (
    <g>
      <circle cx="9.5" cy="19.5" r="1.4" />
      <circle cx="16.5" cy="19.5" r="1.4" />
      <path d="M3 4h2.4l2.2 10.5h9.8L20 7.5H6" />
    </g>
  ),
  trend: (
    <g>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M15 7h6v6" />
    </g>
  ),
  chat: <path d="M4 5.5h16v11h-11l-5 4v-15z" />,
  wrench: (
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.7-3.8a6 6 0 0 1-7.9 7.9L6.6 20.3a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.9-7.9l-3.7 3.8z" />
  ),
  flask: (
    <g>
      <path d="M10 2.5v7l-5.2 11a1 1 0 0 0 .9 1.5h12.6a1 1 0 0 0 .9-1.5L14 9.5v-7" />
      <path d="M8.5 2.5h7" />
      <path d="M7 16h10" />
    </g>
  ),
  megaphone: (
    <g>
      <path d="M3 11l18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </g>
  ),
  book: <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />,
  globe: (
    <g>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 2.5a14.5 14.5 0 0 0 0 19 14.5 14.5 0 0 0 0-19" />
      <path d="M2.5 12h19" />
    </g>
  ),
  bolt: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />,
  target: (
    <g>
      <circle cx="12" cy="12" r="9.5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </g>
  ),
  users: (
    <g>
      <circle cx="9" cy="7.5" r="3.5" />
      <path d="M2.5 20.5v-1.5a4.5 4.5 0 0 1 4.5-4.5h4a4.5 4.5 0 0 1 4.5 4.5v1.5" />
      <path d="M16 4.5a3.5 3.5 0 0 1 0 6.5" />
      <path d="M21.5 20.5v-1.5a4.5 4.5 0 0 0-3-4.2" />
    </g>
  ),
  pen: <path d="M17 3l4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />,
  compass: (
    <g>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M16.2 7.8l-2.1 6.3-6.3 2.1 2.1-6.3 6.3-2.1z" />
    </g>
  ),
  heart: (
    <path d="M12 20.5l-7-7c-1.5-1.5-3-3.2-3-5.5A5.5 5.5 0 0 1 7.5 2.5c1.8 0 3 .5 4.5 2 1.5-1.5 2.7-2 4.5-2A5.5 5.5 0 0 1 22 8c0 2.3-1.5 4-3 5.5l-7 7z" />
  ),
};

type TeamIconProps = {
  name: TeamIconName;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Minimal stroke line icon marking a Team — 2px stroke, round caps, inherits
 * currentColor. User-chosen per Team (stored on `desks.icon`, picked in Team
 * settings); the autonomy tier is NOT in the icon (it lives on the Team page).
 *
 * @example
 * <TeamIcon name="shield" />
 */
export function TeamIcon({ name, size = 15, className, style }: TeamIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      className={className}
      style={{
        flex: "none",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 2,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        opacity: 0.85,
        ...style,
      }}
    >
      {PATHS[name]}
    </svg>
  );
}

export { TEAM_ICON_NAMES };
export type { TeamIconName, TeamIconProps };
